/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.
*/

import { CommonModule } from "@angular/common";
import {
	Component,
	type ElementRef,
	EventEmitter,
	type OnInit,
	Output,
	effect,
	forwardRef,
	input,
	signal,
	viewChild,
} from "@angular/core";
import {
	type ControlValueAccessor,
	FormsModule,
	NG_VALUE_ACCESSOR,
} from "@angular/forms";
import { provideNgxMask } from "ngx-mask";
import { IconComponent } from "../icon/icon.component";

// ... (const noop, CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR)
const noop = () => { };

const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => InputNumberComponent),
	multi: true,
};

/**
 * Componente InputNumber - Gestisce input numerici con formattazione localizzata
 *
 * === FUNZIONALITÀ PRINCIPALI ===
 *
 * 🌍 LOCALIZZAZIONE:
 * - Formattazione automatica dei numeri in base al locale (separatori decimali e delle migliaia)
 * - Supporto per modalità decimale e valuta
 * - Separatori personalizzati per migliaia e decimali (es: it-IT usa '.' per migliaia e ',' per decimali)
 *
 * ✅ VALIDAZIONE:
 * - Validazione min/max con correzione automatica
 * - Parsing intelligente dell'input utente
 * - Gestione di casi speciali (input vuoto, solo separatore decimale, etc.)
 *
 * 🎯 GESTIONE CURSORE:
 * - Posizionamento intelligente del cursore dopo la formattazione
 * - Preservazione dell'intento dell'utente durante la digitazione
 * - Gestione speciale per backspace nelle cifre decimali
 *
 * ⌨️ CONTROLLI:
 * - Incremento/decremento con frecce direzionali o pulsanti
 * - Gestione tasti speciali (backspace, separatore decimale)
 * - Pulsante di reset opzionale
 *
 * 🔗 INTEGRAZIONE ANGULAR:
 * - Implementa ControlValueAccessor per integrazione con Angular Forms
 * - Eventi di output per blur e change
 * - Supporto per disabled, required, error states
 *
 * === FLUSSO DI FUNZIONAMENTO ===
 *
 * 1. L'utente digita → onInput() → parsing → validazione → formattazione → aggiornamento UI
 * 2. Tasti speciali → onKeyDown()/onInputKeyPress() → gestione specifica
 * 3. Perdita focus → onBlurHandler() → formattazione finale
 * 4. Cambio valore esterno → writeValue() → aggiornamento interno
 *
 * === DEBUG ===
 * I console.log sono stati aggiunti con emoji distintive per tracciare facilmente:
 * 🔧 Inizializzazione, 🔄 Aggiornamenti, ⌨️ Input utente, 🔍 Parsing, ✅ Validazione, 📍 Cursore
 */
@Component({
	selector: "cb-input-number",
	standalone: true,
	imports: [FormsModule, IconComponent, CommonModule],
	templateUrl: "./input-number.component.html",
	styleUrl: "./input-number.component.scss",
	providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR, provideNgxMask()],
})
export class InputNumberComponent implements ControlValueAccessor, OnInit {
	// === PROPRIETÀ DI CONFIGURAZIONE ===
	// Input properties - definiscono il comportamento e l'aspetto del componente

	/** Testo placeholder mostrato quando l'input è vuoto */
	public readonly placeholder = input<string>();

	/** Etichetta del campo */
	public readonly label = input<string>();

	/** Se true, il campo riceve automaticamente il focus */
	public readonly autofocus = input<boolean>();

	/** Testo di aiuto sotto il campo */
	public readonly hint = input<string>();

	/** Se true, il campo è obbligatorio */
	public readonly required = input<boolean>();

	/** Se true, il campo è disabilitato */
	public readonly disabled = input(false);

	/** Valore minimo accettato */
	public readonly min = input<number>();

	/** Valore massimo accettato */
	public readonly max = input<number>();

	/** Se true, mostra il campo in stato di errore */
	public readonly error = input<boolean>();

	/** Locale per la formattazione (es: 'it-IT', 'en-US') */
	public readonly locale = input<string>("it-IT");

	/** Modalità di formattazione: decimale o valuta */
	public readonly mode = input<"decimal" | "currency">("decimal");

	/** Codice valuta (necessario se mode='currency') */
	public readonly currency = input<string>();

	/** Numero minimo di cifre decimali da mostrare */
	public readonly minFractionDigits = input<number>();

	/** Numero massimo di cifre decimali da mostrare */
	public readonly maxFractionDigits = input<number>();

	/** Icona informativa accanto al campo */
	public readonly info = input<string>();

	/** Prefisso mostrato all'interno del campo (lato sinistro) */
	public readonly prefix = input<string>();

	/** Suffisso mostrato all'interno del campo (lato destro) */
	public readonly suffix = input<string>();

	/** Testo mostrato prima del campo (esterno) */
	public readonly leadingText = input<string>();

	/** Testo mostrato dopo il campo (esterno) */
	public readonly trailingText = input<string>();

	/** Icona mostrata nel campo */
	public readonly icon = input<string>();

	/** Se true, mostra i pulsanti di incremento/decremento */
	public readonly buttons = input<boolean>();

	/** Posizione dei pulsanti: 'trailing' (a destra) o 'divided' (separati) */
	public readonly buttonsType = input<"trailing" | "divided">("trailing");

	/** Se true, mostra il pulsante di reset */
	public readonly resetButton = input<boolean>(false);

	// === RIFERIMENTI AGLI ELEMENTI DOM ===
	/** Riferimento all'elemento prefix nel template */
	readonly prefixElement = viewChild<ElementRef>("Prefix");

	/** Riferimento all'elemento input nel template (obbligatorio) */
	readonly inputElement = viewChild.required<ElementRef>("Input");

	/** Riferimento all'elemento suffix nel template */
	readonly suffixElement = viewChild<ElementRef>("Suffix");

	// === EVENTI OUTPUT ===
	/** Emesso quando il campo perde il focus */
	@Output() public blur: EventEmitter<Event> = new EventEmitter<Event>();

	/** Emesso quando il valore cambia */
	@Output() public change: EventEmitter<number | null> = new EventEmitter<
		number | null
	>();

	// === STATO INTERNO ===
	/** Valore formattato mostrato all'utente */
	public displayedValue = signal<string>("");

	/** Valore numerico interno (modello) - può essere null se l'input è vuoto */
	private innerValue: number | null = null;

	/** Formatter per i numeri basato sul locale corrente */
	private numberFormatter = signal<Intl.NumberFormat>(
		new Intl.NumberFormat(this.locale()),
	);

	/** Separatore decimale per il locale corrente (es: '.' per en-US, ',' per it-IT) */
	private decimalSeparator = signal<string>(".");

	/** Separatore delle migliaia per il locale corrente (es: ',' per en-US, '.' per it-IT) */
	private groupSeparator = signal<string>(",");

	/**
	 * Constructor - Inizializza gli effect per la gestione reattiva
	 */
	constructor() {
		// Effect per aggiornare la configurazione del formatter quando cambiano le proprietà
		effect(() => {
			this.updateFormatterConfig();
		});

		// Effect per gestire l'autofocus
		effect(() => {
			if (this.autofocus()) {
				setTimeout(() => this.inputElement().nativeElement.focus(), 0);
			}
		});
	}

	/**
	 * Implementazione di OnInit
	 * Garantisce che la formattazione sia corretta anche quando writeValue
	 * viene chiamato prima dell'inizializzazione completa del componente
	 */
	ngOnInit(): void {
		// Se abbiamo un valore interno e il display è vuoto o mal formattato,
		// riformatta il valore ora che tutti gli input sono inizializzati
		if (this.innerValue !== null) {
			// Assicura che il formatter sia configurato correttamente
			this.updateFormatterConfig();
			// Riformatta il valore esistente
			const formattedValue = this.format(this.innerValue);
			this.displayedValue.set(formattedValue);
		}
	}

	// === IMPLEMENTAZIONE CONTROL VALUE ACCESSOR ===
	// Queste sono le funzioni richieste da Angular per l'integrazione con i form reattivi

	/** Callback chiamata quando il valore viene toccato/modificato dall'utente */
	private onTouchedCallback: () => void = noop;

	/** Callback chiamata quando il valore cambia per notificare Angular Forms */
	private onChangeCallback: (_: any) => void = noop;

	/**
	 * Registra la callback per le modifiche - chiamata da Angular Forms
	 * @param fn Funzione da chiamare quando il valore cambia
	 */
	registerOnChange(fn: any): void {
		this.onChangeCallback = fn;
	}

	/**
	 * Registra la callback per il touch - chiamata da Angular Forms
	 * @param fn Funzione da chiamare quando il componente viene toccato
	 */
	registerOnTouched(fn: any): void {
		this.onTouchedCallback = fn;
	}

	/**
	 * Scrive un valore nel componente - chiamata da Angular Forms quando il valore del form cambia
	 * @param value Il nuovo valore da impostare
	 */
	writeValue(value: number | null): void {
		const validatedValue = this.validate(value);
		this.innerValue = Number(validatedValue);

		// Assicura che il formatter sia aggiornato prima della formattazione
		this.updateFormatterConfig();

		const formattedValue = this.format(validatedValue);
		this.displayedValue.set(formattedValue);
	}
	/**
	 * Gestisce l'evento di input dell'utente - FUNZIONE CORE per la gestione della digitazione
	 *
	 * Flusso:
	 * 1. Cattura il valore grezzo digitato dall'utente
	 * 2. Calcola la posizione del cursore rispetto alle cifre numeriche
	 * 3. Pulisce, analizza e valida il valore
	 * 4. Aggiorna il modello interno
	 * 5. Riformatta il valore per la visualizzazione
	 * 6. Riposiziona il cursore nella posizione corretta
	 */
	onInput(event: Event): void {
		const inputElement = event.target as HTMLInputElement;

		// Rimuove gli zeri iniziali superflui solo se ci sono altre cifre (es: "000123" -> "123", ma "0" rimane "0")
		const rawValue = inputElement.value.replace(/^0+(?=.)/, "");
		const originalCursor = inputElement.selectionStart ?? 0;

		// Se l'input è vuoto, resetta il componente
		if (rawValue === "") {
			this.reset();
			return;
		}

		// === GESTIONE INTELLIGENTE DEL CURSORE ===
		// Contiamo quante cifre numeriche c'erano prima del cursore nell'input grezzo
		// Questo ci permette di riposizionare correttamente il cursore dopo la formattazione
		const digitsBeforeCursor = (
			rawValue.substring(0, originalCursor).match(/[0-9]/g) || []
		).length;

		// Gestione speciale per l'inserimento del separatore decimale
		// Verifica se l'utente sta aggiungendo la virgola/punto decimale
		const currentDisplayed = this.displayedValue() || "";
		const decimalSeparator = this.decimalSeparator();
		const hasDecimalInRaw = rawValue.includes(decimalSeparator);
		const hasDecimalInDisplayed = currentDisplayed.includes(decimalSeparator);

		const userIsTypingDecimal = hasDecimalInRaw && !hasDecimalInDisplayed;

		// === FASE 1: PARSING E VALIDAZIONE ===
		const parsedValue = this.parse(rawValue);
		const validatedValue = this.validate(parsedValue);

		// === FASE 2: AGGIORNAMENTO DEL MODELLO ===
		if (this.innerValue !== validatedValue) {
			this.innerValue = validatedValue;
			this.onChangeCallback(validatedValue);
			this.change.emit(validatedValue);
		}

		// === FASE 3: FORMATTAZIONE PER LA VISUALIZZAZIONE ===
		let formattedValue = this.format(this.innerValue);

		// Se l'utente ha appena digitato il separatore decimale, assicurati che sia visibile
		if (
			userIsTypingDecimal &&
			!formattedValue.includes(this.decimalSeparator())
		) {
			formattedValue += this.decimalSeparator();
		}

		// === FASE 4: CALCOLO NUOVA POSIZIONE CURSORE ===
		// Trova la posizione nella stringa formattata dopo lo stesso numero di cifre
		let newCursorPos = 0;
		let digitsPassed = 0;

		for (const char of formattedValue) {
			if (digitsPassed === digitsBeforeCursor) {
				break; // Trovata la posizione corretta
			}
			newCursorPos++;
			if (/[0-9]/.test(char)) {
				digitsPassed++;
			}
		}

		// Se l'utente ha appena digitato il separatore decimale, posiziona il cursore dopo di esso
		if (userIsTypingDecimal && formattedValue.includes(decimalSeparator)) {
			const decimalIndex = formattedValue.indexOf(decimalSeparator);
			newCursorPos = decimalIndex + 1;
		}
		// Se l'utente sta inserendo testo (non cancellando) e il cursore è su un separatore
		// di gruppo, spostalo a destra del separatore
		else if (
			formattedValue.length > this.displayedValue().length &&
			formattedValue.charAt(newCursorPos) === this.groupSeparator()
		) {
			newCursorPos++;
		}

		// === FASE 5: AGGIORNAMENTO UI ===
		this.displayedValue.set(formattedValue);

		// Aggiorna l'input DOM in modo asincrono per evitare conflitti
		Promise.resolve().then(() => {
			inputElement.value = formattedValue;
			inputElement.setSelectionRange(newCursorPos, newCursorPos);
		});
	}

	/**
	 * Gestisce la pressione di tasti speciali durante la digitazione
	 *
	 * Principalmente gestisce:
	 * - Separatore decimale: se già presente, sposta solo il cursore
	 */
	onInputKeyPress(event: KeyboardEvent) {
		if (this.disabled()) {
			return;
		}

		const key = event.key;
		const inputElement = event.target as HTMLInputElement;
		const decimalSep = this.decimalSeparator();

		// === GESTIONE SEPARATORE DECIMALE ===
		if (key === decimalSep) {
			const currentValue = inputElement.value;
			const cursorPos = inputElement.selectionStart ?? 0;
			const decimalIndex = currentValue.indexOf(decimalSep);

			// Se esiste già un separatore decimale nel valore
			if (decimalIndex !== -1) {
				event.preventDefault();
				const newCursorPos = decimalIndex + 1;
				inputElement.setSelectionRange(newCursorPos, newCursorPos);

				// Sposta il cursore alla destra del separatore decimale esistente

				return;
			}
		}
	}

	/**
	 * Gestisce la pressione di tasti speciali (frecce, backspace, etc.)
	 *
	 * Funzionalità:
	 * - Freccia Su/Giù: incrementa/decrementa il valore
	 * - Backspace: gestione intelligente per le cifre decimali
	 */
	onKeyDown(event: KeyboardEvent) {
		if (this.disabled()) {
			return;
		}

		const key = event.key;
		const inputElement = event.target as HTMLInputElement;

		// === GESTIONE FRECCE DIREZIONALI ===
		if (key === "ArrowUp") {
			event.preventDefault();
			this.increment();
			return;
		}
		if (key === "ArrowDown") {
			event.preventDefault();
			this.decrement();
			return;
		}

		// === GESTIONE BACKSPACE INTELLIGENTE ===
		if (key === "Backspace") {
			const valueStr = inputElement.value;
			const cursorPos = inputElement.selectionStart ?? 0;
			const decimalSep = this.decimalSeparator();
			const decimalSepIndex = valueStr.indexOf(decimalSep);

			// Se il cursore è nella parte decimale, gestione speciale
			if (decimalSepIndex !== -1 && cursorPos > decimalSepIndex) {
				event.preventDefault();

				// Se il cursore è subito dopo il separatore decimale, rimuovi la parte decimale
				if (cursorPos === decimalSepIndex + 1) {
					const newValue = Math.trunc(this.innerValue ?? 0);
					this.updateValue(newValue, { newCursorPos: decimalSepIndex });
					return;
				}

				if (this.innerValue === null) return;

				const minDigits = this.minFractionDigits() ?? 0;
				const displayedValue = valueStr;
				const decimalPart = displayedValue.substring(decimalSepIndex + 1);

				// Calcola quale cifra decimale eliminare (0-based index nella parte decimale)
				const decimalDigitIndex = cursorPos - decimalSepIndex - 2; // -1 posizione dopo separatore, -1 per backspace

				if (decimalDigitIndex < 0 || decimalDigitIndex >= decimalPart.length)
					return;

				// Rimuovi o sostituisci la cifra decimale
				let newDecimalPart: string;
				if (decimalPart.length > minDigits) {
					// Rimuove completamente la cifra
					newDecimalPart =
						decimalPart.slice(0, decimalDigitIndex) +
						decimalPart.slice(decimalDigitIndex + 1);
				} else {
					// Sostituisce con 0 per mantenere il numero minimo di cifre
					newDecimalPart = `${decimalPart.slice(0, decimalDigitIndex)}0${decimalPart.slice(decimalDigitIndex + 1)}`;
				}

				// Ricostruisci il numero completo
				const integerPart = displayedValue.substring(0, decimalSepIndex);
				const newDisplayValue = `${integerPart}${this.decimalSeparator()}${newDecimalPart}`;
				const newInnerValue = this.parse(newDisplayValue);

				this.updateValue(newInnerValue, { newCursorPos: cursorPos - 1 });
			}
		}
	}

	/**
	 * Gestisce l'evento blur (perdita di focus)
	 * Finalizza la formattazione e notifica Angular Forms
	 */
	onBlurHandler(event: Event) {
		// Notifica Angular Forms che il campo è stato toccato
		this.onTouchedCallback();

		// Emette l'evento blur per i listener esterni
		this.blur.emit(event);

		// Applica la formattazione finale (rimuove eventuali separatori temporanei)
		const finalFormattedValue = this.format(this.innerValue);
		this.displayedValue.set(finalFormattedValue);
		(event.target as HTMLInputElement).value = finalFormattedValue;
	}

	// === METODI DI UTILITÀ PRIVATI ===

	/**
	 * Aggiorna la configurazione del formatter in base al locale e alle opzioni correnti
	 * Chiamato automaticamente quando cambiano le proprietà di configurazione
	 */
	private updateFormatterConfig(): void {
		const locale = this.locale();
		const minFraction = this.minFractionDigits();
		const maxFraction = this.maxFractionDigits();

		// Configura le opzioni del formatter Intl.NumberFormat
		const options: Intl.NumberFormatOptions = {
			style: this.mode(),
			currency: this.currency(),
			useGrouping: true, // Abilita i separatori delle migliaia
			minimumFractionDigits: minFraction,
			maximumFractionDigits: maxFraction,
		};

		const formatter = new Intl.NumberFormat(locale, options);
		this.numberFormatter.set(formatter);

		// Estrae i separatori specifici del locale corrente
		const parts = new Intl.NumberFormat(locale).formatToParts(1234.5);
		const groupSep = parts.find((p) => p.type === "group")?.value ?? ",";
		const decimalSep = parts.find((p) => p.type === "decimal")?.value ?? ".";

		this.groupSeparator.set(groupSep);
		this.decimalSeparator.set(decimalSep);
	}

	/**
	 * Formatta un numero per la visualizzazione usando il formatter configurato
	 * @param value Il numero da formattare
	 * @returns La stringa formattata o vuota se value è null
	 */
	private format(value: number | null): string {
		if (value === null) {
			return "";
		}

		const formatted = this.numberFormatter().format(value);
		return formatted;
	}

	/**
	 * Analizza una stringa di input dell'utente e la converte in un numero
	 *
	 * Processo:
	 * 1. Gestisce casi speciali (null, vuoto, solo separatore decimale)
	 * 2. Rimuove i separatori delle migliaia
	 * 3. Normalizza il separatore decimale a '.'
	 * 4. Rimuove caratteri non numerici
	 * 5. Converte in numero
	 *
	 * @param value La stringa da analizzare
	 * @returns Il numero analizzato o null se non valido
	 */
	private parse(value: string): number | null {
		if (value === null || value.trim() === "") {
			return null;
		}

		const decimalSep = this.decimalSeparator();
		const groupSep = this.groupSeparator();

		// Caso speciale: solo separatore decimale = 0
		if (value.trim() === decimalSep) {
			return 0;
		}

		// Rimuove i separatori delle migliaia e normalizza il separatore decimale
		const cleanValue = value
			.replace(new RegExp(`\\${groupSep}`, "g"), "") // Rimuove separatori migliaia
			.replace(decimalSep, "."); // Normalizza separatore decimale

		// Rimuove tutti i caratteri non numerici eccetto punto e segno meno
		const sanitizedValue = cleanValue.replace(/[^0-9.-]/g, "");

		const parsed = Number.parseFloat(sanitizedValue);
		const result = Number.isNaN(parsed) ? this.innerValue : parsed;

		return result;
	}

	/**
	 * Valida un numero rispetto ai vincoli min/max
	 * @param value Il numero da validare
	 * @returns Il numero validato (eventualmente corretto ai limiti)
	 */
	private validate(value: number | null): number | null {
		if (value === null) {
			return null;
		}

		const min = this.min();
		const max = this.max();

		// Applica il limite minimo
		if (min !== undefined && value < min) {
			return min;
		}

		// Applica il limite massimo
		if (max !== undefined && value > max) {
			return max;
		}

		return value;
	}

	// === METODI PUBBLICI DI CONTROLLO ===

	/**
	 * Resetta il componente al suo stato iniziale (valore null)
	 * Utilizzato anche internamente quando l'input diventa vuoto
	 */
	reset() {
		this.innerValue = null;
		this.displayedValue.set("");
		this.onChangeCallback(null);
		this.change.emit(null);
		this.inputElement().nativeElement.focus();
	}

	/**
	 * Incrementa il valore di 1
	 * Rispetta i limiti min/max configurati
	 */
	increment() {
		const currentValue = this.innerValue ?? 0;
		let newValue = currentValue + 1;

		newValue = this.validate(newValue) ?? newValue;
		this.updateValue(newValue, { setCursorToEnd: true });
	}

	/**
	 * Decrementa il valore di 1
	 * Rispetta i limiti min/max configurati
	 */
	decrement() {
		const currentValue = this.innerValue ?? 0;
		let newValue = currentValue - 1;

		newValue = this.validate(newValue) ?? newValue;
		this.updateValue(newValue, { setCursorToEnd: true });
	}

	/**
	 * Metodo centrale per aggiornare il valore del componente
	 * Gestisce validazione, formattazione, aggiornamento UI e posizionamento cursore
	 *
	 * @param newValue Il nuovo valore da impostare
	 * @param options Opzioni per il posizionamento del cursore
	 */
	private updateValue(
		newValue: number | null,
		options: { newCursorPos?: number; setCursorToEnd?: boolean } = {},
	) {
		const validatedValue = this.validate(newValue);
		this.innerValue = validatedValue;

		const displayValue =
			validatedValue === null ? "" : this.format(validatedValue);

		// Aggiorna il signal e notifica i cambiamenti
		this.displayedValue.set(displayValue);
		this.onChangeCallback(validatedValue);
		this.change.emit(validatedValue);

		// Aggiorna l'elemento DOM in modo asincrono
		Promise.resolve().then(() => {
			const inputEl = this.inputElement().nativeElement;
			inputEl.value = displayValue;
			inputEl.focus();

			// Gestione posizionamento cursore
			if (options.newCursorPos !== undefined) {
				const finalCursorPos = Math.max(0, options.newCursorPos);
				inputEl.setSelectionRange(finalCursorPos, finalCursorPos);
			} else if (options.setCursorToEnd) {
				const end = displayValue.length;
				inputEl.setSelectionRange(end, end);
			}
		});
	}
}
